<script setup lang="ts">
/**
 * Racine de l'app : choisit le layout d'après `meta.layout` (§8.1).
 *
 * `<Toaster />` et `<ConfirmDialogHost />` sont montés ICI, HORS du composant de
 * layout : un layout conditionnel les démonterait et ferait disparaître les
 * toasts en vol (§5.4).
 *
 * ⚠️ L'écran d'erreur global vit ICI et pas dans `AppLayout` : la navigation
 * peut viser une route en layout `minimal` (`/`, 404, `/forbidden`), où
 * `AppLayout` n'est jamais monté. Le guard laisse passer sur `status === 'error'`
 * (§8.3) — c'est ce `v-if` qui empêche la page de destination de se rendre.
 */
import { computed } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import { useAuthStore } from '@/auth/authStore';
import AppLayout from '@/layouts/AppLayout.vue';
import MinimalLayout from '@/layouts/MinimalLayout.vue';
import ErrorAlert from '@/components/app/ErrorAlert.vue';
import Toaster from '@/components/app/Toaster.vue';
import ConfirmDialogHost from '@/components/app/ConfirmDialogHost.vue';
import { Button } from '@/components/ui/button';

const route = useRoute();
const auth = useAuthStore();
const layout = computed(() => (route.meta.layout === 'minimal' ? MinimalLayout : AppLayout));

// Rechargement complet : le guard a laissé passer sans matrice de rôles.
function reload(): void {
  window.location.reload();
}
</script>

<template>
  <div
    v-if="auth.status === 'error'"
    class="flex min-h-screen items-center justify-center bg-pbg px-6"
  >
    <div class="w-full max-w-md">
      <ErrorAlert
        title="Session indisponible"
        :message="auth.error ?? 'Impossible de charger votre profil.'"
        retryable
        @retry="reload"
      />
      <div class="mt-3 flex justify-end">
        <Button type="button" variant="outline" size="sm" @click="auth.signOut()">
          Se déconnecter
        </Button>
      </div>
    </div>
  </div>

  <component :is="layout" v-else>
    <RouterView />
  </component>

  <Toaster />
  <ConfirmDialogHost />
</template>
